import { bpmsCompatible, isValidBpm } from '@/audio/clipTiming';
import {
  chooseFillTargetBpm, fisherYates, hasPositiveBars, selectFillLoops,
} from '@/components/Admin/ClassPackBuilder';

const GROUPS = ['melody', 'drums', 'texture', 'fx'];
const LIMITS = { melody: 20, drums: 10, texture: 10, fx: 10 };

const taggedKey = (value) => {
  const key = String(value || '').trim();
  return key && key !== '-' ? key : null;
};

const validLoop = (loop) => loop && String(loop.id || '').trim()
  && hasPositiveBars(loop.bars) && isValidBpm(Number(loop.bpm));

const snapshotLoop = (loop, group) => ({
  id: String(loop.id),
  name: String(loop.name || loop.id),
  bpm: Number(loop.bpm),
  bars: Number(loop.bars),
  key: taggedKey(loop.key),
  instrument_group: group,
});

// The server hashes this same canonical value. The UI only needs equality, so
// keeping the sorted ids as a stable string avoids making generation async.
export function seatPackFingerprint(pack) {
  return JSON.stringify(GROUPS.flatMap((group) => (
    pack?.slots?.[group] || []
  )).map((loop) => String(loop.id)).sort());
}

function leastUsedFirst(candidates, usage, random) {
  const byCount = new Map();
  candidates.forEach((loop) => {
    const count = Number(usage[loop.id]) || 0;
    if (!byCount.has(count)) byCount.set(count, []);
    byCount.get(count).push(loop);
  });
  return [...byCount.keys()].sort((a, b) => a - b)
    .flatMap((count) => fisherYates(byCount.get(count), random));
}

function suppliedTempos(melodies) {
  const counts = new Map();
  melodies.forEach((loop) => {
    if (Number(loop.bars) !== 8) return;
    const bpm = Number(loop.bpm);
    counts.set(bpm, (counts.get(bpm) || 0) + 1);
  });
  const eligible = [...counts.entries()].filter(([, count]) => count >= LIMITS.melody);
  if (eligible.length) return eligible.sort((a, b) => a[0] - b[0]);
  const fallback = chooseFillTargetBpm('', melodies);
  return fallback == null ? [] : [[fallback, counts.get(fallback) || 1]];
}

function tempoSchedule(tempos, seatCount, random) {
  const totalSupply = tempos.reduce((sum, [, count]) => sum + count, 0);
  const assigned = new Map(tempos.map(([bpm]) => [bpm, 0]));
  return Array.from({ length: seatCount }, (_, index) => {
    const scored = tempos.map(([bpm, supply]) => ({
      bpm,
      score: (supply / totalSupply) * (index + 1) - assigned.get(bpm),
    }));
    const best = Math.max(...scored.map((item) => item.score));
    const choices = fisherYates(scored.filter((item) => Math.abs(item.score - best) < 1e-9), random);
    const bpm = choices[0].bpm;
    assigned.set(bpm, assigned.get(bpm) + 1);
    return bpm;
  });
}

function preferredKeyForSeat(melodies, bpm, keyUse, random) {
  const counts = new Map();
  melodies.forEach((loop) => {
    const key = taggedKey(loop.key);
    if (!key || Number(loop.bars) !== 8 || !bpmsCompatible(loop.bpm, bpm)) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  if (!counts.size) return null;
  const enough = [...counts.entries()].filter(([, count]) => count >= LIMITS.melody);
  const choices = enough.length ? enough : [...counts.entries()];
  const scored = choices.map(([key, supply]) => ({
    key,
    score: (keyUse.get(`${bpm}:${key}`) || 0) / supply,
    supply,
  }));
  const best = Math.min(...scored.map((item) => item.score));
  const tied = scored.filter((item) => item.score === best)
    .sort((a, b) => b.supply - a.supply || a.key.localeCompare(b.key));
  const selected = fisherYates(tied.filter((item) => item.supply === tied[0].supply), random)[0];
  keyUse.set(`${bpm}:${selected.key}`, (keyUse.get(`${bpm}:${selected.key}`) || 0) + 1);
  return selected.key;
}

function makePack({ catalog, bpm, seatNumber, usage, keyUse, random, excludeIds = new Set() }) {
  const slots = { melody: [], drums: [], texture: [], fx: [] };
  const preferredKey = preferredKeyForSeat(catalog.melody, bpm, keyUse, random);
  const usedIds = new Set();
  GROUPS.forEach((group) => {
    // Give selectFillLoops the whole tagged group: its tiers already prefer tempo-compatible
    // 8-bar loops and fall back to near matches (time stretch covers the tempo). Filtering to the
    // coherent tier here made a whole class fail when one group was short at a tempo (the real
    // catalog has 9 tagged 8-bar textures at 70 BPM).
    // selectFillLoops owns the Sprint 1 compatibility, bar-length, and melody-key
    // tier rules. The candidates already carry the randomized least-used order;
    // a no-op shuffle preserves that order inside each tier.
    const pick = (candidates) => selectFillLoops({
      group,
      candidates,
      existing: [],
      usedIds,
      limit: LIMITS[group],
      targetBpm: bpm,
      targetKey: preferredKey,
      random: () => 0.999999999,
    });
    // A Beat 2 pack leaves out the seat's Beat 1 loops entirely. Sorting them last was not
    // enough: selectFillLoops re-orders by tier, so Beat 2 kept picking the same sounds.
    // Only a group that cannot fill without them falls back to the whole list.
    const fresh = excludeIds.size
      ? pick(leastUsedFirst(catalog[group].filter((loop) => !excludeIds.has(loop.id)), usage, random))
      : null;
    const selected = fresh && fresh.length === LIMITS[group]
      ? fresh
      : pick(leastUsedFirst(catalog[group], usage, random));
    slots[group] = selected.map((loop) => snapshotLoop(loop, group));
    slots[group].forEach((loop) => usedIds.add(loop.id));
  });
  if (GROUPS.some((group) => slots[group].length !== LIMITS[group])) return null;
  const key = taggedKey(slots.melody[0]?.key) || preferredKey;
  return {
    id: `seat-${seatNumber}-pack`,
    name: `Seat ${seatNumber} pack`,
    bpm,
    key,
    slots,
  };
}

function failureMessage(seatCount) {
  return `The tagged library can't make ${seatCount} different packs right now. Try fewer seats, Same pack for everyone, or tag more loops.`;
}

export function seatPackStats(packs) {
  const tempos = {};
  const loopCounts = new Map();
  const idSets = packs.map((pack) => {
    tempos[pack.bpm] = (tempos[pack.bpm] || 0) + 1;
    const ids = new Set(GROUPS.flatMap((group) => pack.slots[group].map((loop) => loop.id)));
    ids.forEach((id) => loopCounts.set(id, (loopCounts.get(id) || 0) + 1));
    return ids;
  });
  let shared = 0;
  let pairs = 0;
  for (let left = 0; left < idSets.length; left += 1) {
    for (let right = left + 1; right < idSets.length; right += 1) {
      pairs += 1;
      idSets[left].forEach((id) => { if (idSets[right].has(id)) shared += 1; });
    }
  }
  return {
    tempos,
    maxShare: loopCounts.size ? Math.max(...loopCounts.values()) : 0,
    avgSharedPerPair: pairs ? shared / pairs : 0,
  };
}

export function generateSeatPacks({
  catalog,
  seatCount,
  usage = {},
  existingFingerprints = [],
  excludedIdsBySeat = [],
  random = Math.random,
}) {
  const count = Number(seatCount);
  if (!Number.isInteger(count) || count < 1 || count > 40) throw new Error(failureMessage(seatCount));
  const usableCatalog = Object.fromEntries(GROUPS.map((group) => [
    group,
    (Array.isArray(catalog?.[group]) ? catalog[group] : []).filter(validLoop),
  ]));
  const tempos = suppliedTempos(usableCatalog.melody);
  if (!tempos.length) throw new Error(failureMessage(count));
  const schedule = tempoSchedule(tempos, count, random);
  const nextUsage = { ...usage };
  const fingerprints = new Set(existingFingerprints);
  const keyUse = new Map();
  const packs = [];
  for (let index = 0; index < count; index += 1) {
    let accepted = null;
    for (let attempt = 0; attempt < 25 && !accepted; attempt += 1) {
      const candidate = makePack({
        catalog: usableCatalog,
        bpm: schedule[index],
        seatNumber: index + 1,
        usage: nextUsage,
        keyUse,
        random,
        excludeIds: excludedIdsBySeat[index] || new Set(),
      });
      if (candidate && !fingerprints.has(seatPackFingerprint(candidate))) {
        const excluded = excludedIdsBySeat[index] || new Set();
        const candidateIds = new Set(GROUPS.flatMap((group) => candidate.slots[group].map((item) => item.id)));
        const overlap = [...candidateIds].filter((id) => excluded.has(id)).length;
        if (!excluded.size || overlap / Math.min(excluded.size, candidateIds.size) <= 0.5) accepted = candidate;
      }
    }
    if (!accepted) throw new Error(failureMessage(count));
    const fingerprint = seatPackFingerprint(accepted);
    fingerprints.add(fingerprint);
    GROUPS.forEach((group) => accepted.slots[group].forEach((loop) => {
      nextUsage[loop.id] = (Number(nextUsage[loop.id]) || 0) + 1;
    }));
    packs.push(accepted);
  }
  return { packs, stats: seatPackStats(packs) };
}

export function seatPackLoopIds(pack) {
  return new Set(GROUPS.flatMap((group) => (pack?.slots?.[group] || []).map((loop) => loop.id)));
}

export function generateSeatPackPairs({ catalog, seatCount, usage = {}, random = Math.random }) {
  const first = generateSeatPacks({ catalog, seatCount, usage, random });
  const nextUsage = { ...usage };
  first.packs.forEach((pack) => seatPackLoopIds(pack).forEach((id) => { nextUsage[id] = (Number(nextUsage[id]) || 0) + 1; }));
  const second = generateSeatPacks({
    catalog,
    seatCount,
    usage: nextUsage,
    existingFingerprints: first.packs.map(seatPackFingerprint),
    excludedIdsBySeat: first.packs.map(seatPackLoopIds),
    random,
  });
  return {
    pairs: first.packs.map((pack, index) => ({ pack, pack2: second.packs[index] })),
    stats: seatPackStats(first.packs),
  };
}
